<template>
    <div 
      class="tooltip" 
      ref="tooltip"
      data-testid="tooltip">
      <div 
        v-if="isOnboarding"
        class="tooltip__close-button"
        @click="closeTooltips"
        data-testid="close">
        <Union/>
      </div>
      <div v-if="isOnboarding" class="tooltip__content" :style="tooltipContentStyle">
        <p v-if="text">{{ text }}</p>
        <label 
          v-if="confirmText"
          class="tooltip__confirm-button" 
          @click="nextTooltip"
          data-testid="confirm">
          {{ confirmText }}
        </label>
      </div>
      <slot />
      <div id="arrow" class="tooltip__arrow" data-popper-arrow></div>
    </div>
</template>
<script setup lang="ts">
import { computed, CSSProperties, onMounted, onUnmounted, ref, watch } from 'vue'
import * as Popper from '@popperjs/core'
import Union from '@/assets/Union.svg'

interface Props {
  referenceElementId: string
  text?: string,
  placement: Popper.Placement
  confirmText?: string,
  isActive: boolean,
  offset?: number,
  width?: number,
  paddingRight?: number,
  isOnboarding?: boolean
}

const props = withDefaults(defineProps<Props>(),
  {
    offset: 0,
    width: 224,
    paddingRight: 30,
    isOnboarding: false
  })

const emits = defineEmits<{
  (event: 'next'): void
  (event: 'close'): void
}>()

const popper = ref<Popper.Instance | null>(null)

const nextTooltip = () => {
  popper.value?.destroy()
  emits('next')
}

const closeTooltips = () => {
  emits('close')
}

const tooltipContentStyle = computed<CSSProperties>(() => {
  return {
    width: props.width + 'px',
    paddingRight: props.paddingRight + 'px'
  }
})

const tooltip = ref<HTMLElement | null>(null)
onMounted(() => {
  if (props.isOnboarding) {
    createTooltip()
  }
})

onUnmounted(() => {
  popper.value?.destroy()
})

const createTooltip = () => {
  const elem: HTMLElement | null = document.getElementById(props.referenceElementId)
  if (elem && tooltip.value) {
    popper.value = Popper.createPopper(elem, tooltip.value, {
      placement: props.placement,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [props.offset, 23] // controls the offset of the tooltip (along the reference, away from the reference)
          }
        },
        {
          name: 'arrow',
          options: {
            padding: 14 // controls how close the arrow can get to the edge of the tooltip
          }
        },
        {
          name: 'preventOverflow',
          options: {
            tetherOffset: 20, // controls how close to the edge of the reference element the tooltip can be
            padding: 14 // controls how far the tooltip is from the edges of the viewport
          }
        }
      ]
    })
  }
}

watch(() => props.isActive, () => {
  if (props.isActive) {
    if (!props.isOnboarding) {
      createTooltip()
    } else {
      tooltip.value?.setAttribute('data-show', 'true')
      popper.value?.update()
    }
  } else {
    tooltip.value?.removeAttribute('data-show')
    popper.value?.destroy()
  }
})

</script>
<style lang="scss">
.tooltip {
  background: $white;
  border-radius: 8px;
  padding: 4px;
  z-index: 2;
  opacity: 0;

  &[data-show] {
    opacity: 1;
    transition: opacity 500ms $animationCurve;
  }

  &__close-button {
    width: 16px;
    height: 16px;
    position: absolute;
    right: 4px;
    top: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 50%;
    background-color: $grey-30;
    >svg {
      fill: $grey-60
    }
  }

  &__content {
    padding: 12px;
    font-size: 14px;
    font-family: $font-bold;
    line-height: 17.08px;
    display: flex;
    gap: 10px;
    flex-direction: column;

  }

  &__confirm-button {
    font-size: 12px;
    line-height: 14.65px;
    color: $purple;
    cursor: pointer;
  }

  &__arrow {
    position: absolute;
    width: 12px;
    height: 12px;
    background: inherit;
    visibility: hidden;

    &::before {
      position: absolute;
      width: 12px;
      height: 12px;
      background: inherit;
      visibility: visible;
      content: '';
      transform: rotate(45deg);
    }
  }

  &[data-popper-placement^='top'] > #arrow {
  bottom: -6px;
  }

  &[data-popper-placement^='bottom'] > #arrow {
    top: -6px;
  }

  &[data-popper-placement^='left'] > #arrow {
    right: -6px;
  }

  &[data-popper-placement^='right'] > #arrow {
    left: -6px;
  }
}
</style>
